/**
 * Release Timeline Styling Configuration
 * 
 * Centralized styling configuration for release timeline components
 * including colors, fonts, animations, and responsive design.
 */

import { Theme } from '@mui/material/styles'
import { ReleaseCategory } from '../types/release'

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const RELEASE_TIMELINE_COLORS = {
  // Category colors (matching swimlane configs)
  categories: {
    [ReleaseCategory.SOFTWARE]: {
      primary: '#2196F3',      // Blue
      light: '#E3F2FD',        // Light blue
      dark: '#0D47A1',         // Dark blue
      text: '#0D47A1',         // Dark blue text
      border: '#2196F3',       // Blue border
      hover: '#1976D2',        // Darker blue on hover
      // Dark mode variants
      darkMode: {
        primary: '#64B5F6',    // Lighter blue for dark mode
        light: 'rgba(33, 150, 243, 0.15)',  // Semi-transparent blue background
        dark: '#1976D2',       // Darker blue
        text: '#E3F2FD',       // Light blue text
        border: '#64B5F6',     // Lighter blue border
        hover: '#42A5F5'       // Hover state for dark mode
      }
    },
    [ReleaseCategory.CLOUD]: {
      primary: '#4CAF50',      // Green
      light: '#E8F5E8',        // Light green
      dark: '#1B5E20',         // Dark green
      text: '#1B5E20',         // Dark green text
      border: '#4CAF50',       // Green border
      hover: '#388E3C',        // Darker green on hover
      // Dark mode variants
      darkMode: {
        primary: '#81C784',    // Lighter green for dark mode
        light: 'rgba(76, 175, 80, 0.15)',   // Semi-transparent green background
        dark: '#388E3C',       // Darker green
        text: '#E8F5E8',       // Light green text
        border: '#81C784',     // Lighter green border
        hover: '#66BB6A'       // Hover state for dark mode
      }
    },
    [ReleaseCategory.AZURE]: {
      primary: '#FF9800',      // Orange
      light: '#FFF3E0',        // Light orange
      dark: '#E65100',         // Dark orange
      text: '#E65100',         // Dark orange text
      border: '#FF9800',       // Orange border
      hover: '#F57C00',        // Darker orange on hover
      // Dark mode variants
      darkMode: {
        primary: '#FFB74D',    // Lighter orange for dark mode
        light: 'rgba(255, 152, 0, 0.15)',   // Semi-transparent orange background
        dark: '#F57C00',       // Darker orange
        text: '#FFF3E0',       // Light orange text
        border: '#FFB74D',     // Lighter orange border
        hover: '#FFA726'       // Hover state for dark mode
      }
    },
    [ReleaseCategory.OSS]: {
      primary: '#9C27B0',      // Purple
      light: '#F3E5F5',        // Light purple
      dark: '#4A148C',         // Dark purple
      text: '#4A148C',         // Dark purple text
      border: '#9C27B0',       // Purple border
      hover: '#7B1FA2',        // Darker purple on hover
      // Dark mode variants
      darkMode: {
        primary: '#BA68C8',    // Lighter purple for dark mode
        light: 'rgba(156, 39, 176, 0.15)',  // Semi-transparent purple background
        dark: '#7B1FA2',       // Darker purple
        text: '#F3E5F5',       // Light purple text
        border: '#BA68C8',     // Lighter purple border
        hover: '#AB47BC'       // Hover state for dark mode
      }
    }
  },
  
  // Status colors
  status: {
    released: '#4CAF50',       // Green
    unreleased: '#FF9800',     // Orange
    overdue: '#F44336',        // Red
    upcoming: '#2196F3'        // Blue
  },
  
  // UI colors
  ui: {
    background: '#FAFAFA',     // Light gray background
    surface: '#FFFFFF',        // White surface
    border: '#E0E0E0',         // Light gray border
    divider: '#EEEEEE',        // Divider color
    shadow: 'rgba(0,0,0,0.1)', // Shadow color
    text: {
      primary: '#212121',      // Dark gray
      secondary: '#757575',    // Medium gray
      disabled: '#BDBDBD'      // Light gray
    }
  }
} as const

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const RELEASE_TIMELINE_TYPOGRAPHY = {
  // Timeline bar text
  timelineBar: {
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: 1.2
  },
  
  // Swimlane labels
  swimlaneLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  
  // Category chips
  categoryChip: {
    fontSize: '0.6rem',
    fontWeight: 500,
    lineHeight: 1
  },
  
  // Tooltips
  tooltip: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.4
  },
  
  // Date labels
  dateLabel: {
    fontSize: '0.65rem',
    fontWeight: 500,
    lineHeight: 1.2
  }
} as const

// ============================================================================
// SPACING AND DIMENSIONS
// ============================================================================

export const RELEASE_TIMELINE_DIMENSIONS = {
  // Swimlane dimensions
  swimlane: {
    height: 60,
    spacing: 8,
    borderRadius: 4,
    borderWidth: 1
  },
  
  // Timeline bar dimensions
  timelineBar: {
    minWidth: 20,
    height: 40,
    borderRadius: 3,
    borderWidth: 1
  },
  
  // Label dimensions
  label: {
    width: 100,
    padding: 8,
    margin: 4
  },
  
  // Container dimensions
  container: {
    padding: 16,
    margin: 8,
    borderRadius: 8
  },
  
  // Tooltip dimensions
  tooltip: {
    maxWidth: 300,
    padding: 12,
    borderRadius: 4
  }
} as const

// ============================================================================
// ANIMATION CONFIGURATIONS
// ============================================================================

export const RELEASE_TIMELINE_ANIMATIONS = {
  // Transition durations
  duration: {
    fast: '0.15s',
    normal: '0.3s',
    slow: '0.5s'
  },
  
  // Easing functions
  easing: {
    standard: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0.0, 1, 1)'
  },
  
  // Hover animations
  hover: {
    scale: 1.02,
    shadowElevation: 4
  },
  
  // Loading animations
  loading: {
    duration: '1.5s',
    delay: '0.1s'
  }
} as const

// ============================================================================
// RESPONSIVE BREAKPOINTS
// ============================================================================

export const RELEASE_TIMELINE_BREAKPOINTS = {
  mobile: {
    maxWidth: 767,
    swimlaneHeight: 50,
    labelWidth: 80,
    fontSize: '0.65rem'
  },
  tablet: {
    minWidth: 768,
    maxWidth: 1023,
    swimlaneHeight: 55,
    labelWidth: 90,
    fontSize: '0.7rem'
  },
  desktop: {
    minWidth: 1024,
    swimlaneHeight: 60,
    labelWidth: 100,
    fontSize: '0.75rem'
  }
} as const

// ============================================================================
// STYLE GENERATION FUNCTIONS
// ============================================================================

/**
 * Generate timeline bar styles for a specific category
 */
export function generateTimelineBarStyles(
  category: ReleaseCategory,
  isReleased: boolean = true,
  isHovered: boolean = false,
  theme?: Theme
): React.CSSProperties {
  const categoryColors = RELEASE_TIMELINE_COLORS.categories[category]
  const dimensions = RELEASE_TIMELINE_DIMENSIONS.timelineBar
  const animations = RELEASE_TIMELINE_ANIMATIONS
  
  const baseColor = isReleased ? categoryColors.primary : categoryColors.primary + '80'
  const hoverColor = isHovered ? categoryColors.hover : baseColor
  
  return {
    backgroundColor: hoverColor,
    color: categoryColors.text,
    border: `${dimensions.borderWidth}px solid ${categoryColors.border}`,
    borderRadius: `${dimensions.borderRadius}px`,
    minWidth: `${dimensions.minWidth}px`,
    height: `${dimensions.height}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px',
    fontSize: RELEASE_TIMELINE_TYPOGRAPHY.timelineBar.fontSize,
    fontWeight: RELEASE_TIMELINE_TYPOGRAPHY.timelineBar.fontWeight,
    lineHeight: RELEASE_TIMELINE_TYPOGRAPHY.timelineBar.lineHeight,
    transition: `all ${animations.duration.normal} ${animations.easing.standard}`,
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...(isHovered && {
      transform: `scale(${animations.hover.scale})`,
      boxShadow: `0 ${animations.hover.shadowElevation}px 8px ${categoryColors.primary}30`,
      zIndex: 10
    })
  }
}

/**
 * Generate swimlane container styles
 */
export function generateSwimlaneStyles(
  category: ReleaseCategory,
  isActive: boolean = false,
  theme?: Theme
): React.CSSProperties {
  const categoryColors = RELEASE_TIMELINE_COLORS.categories[category]
  const dimensions = RELEASE_TIMELINE_DIMENSIONS.swimlane
  const animations = RELEASE_TIMELINE_ANIMATIONS
  
  return {
    backgroundColor: categoryColors.light,
    border: `${dimensions.borderWidth}px solid ${categoryColors.border}20`,
    borderRadius: `${dimensions.borderRadius}px`,
    height: `${dimensions.height}px`,
    position: 'relative',
    overflow: 'hidden',
    transition: `all ${animations.duration.normal} ${animations.easing.standard}`,
    ...(isActive && {
      borderColor: categoryColors.border,
      backgroundColor: categoryColors.light,
      boxShadow: `0 2px 8px ${categoryColors.primary}20`
    })
  }
}

/**
 * Generate swimlane label styles
 */
export function generateSwimlaneLabelStyles(
  category: ReleaseCategory,
  theme?: Theme
): React.CSSProperties {
  const categoryColors = RELEASE_TIMELINE_COLORS.categories[category]
  const dimensions = RELEASE_TIMELINE_DIMENSIONS.label
  const typography = RELEASE_TIMELINE_TYPOGRAPHY.swimlaneLabel
  
  return {
    backgroundColor: categoryColors.light,
    color: categoryColors.text,
    border: `1px solid ${categoryColors.border}40`,
    borderRadius: `${RELEASE_TIMELINE_DIMENSIONS.container.borderRadius}px 0 0 ${RELEASE_TIMELINE_DIMENSIONS.container.borderRadius}px`,
    borderRight: 'none',
    width: `${dimensions.width}px`,
    padding: `${dimensions.padding}px`,
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
    lineHeight: typography.lineHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start'
  }
}

/**
 * Generate responsive styles based on container width
 */
export function generateResponsiveStyles(containerWidth: number): {
  swimlaneHeight: number
  labelWidth: number
  fontSize: string
  spacing: number
} {
  const breakpoints = RELEASE_TIMELINE_BREAKPOINTS
  
  if (containerWidth <= breakpoints.mobile.maxWidth) {
    return {
      swimlaneHeight: breakpoints.mobile.swimlaneHeight,
      labelWidth: breakpoints.mobile.labelWidth,
      fontSize: breakpoints.mobile.fontSize,
      spacing: 6
    }
  } else if (containerWidth <= breakpoints.tablet.maxWidth) {
    return {
      swimlaneHeight: breakpoints.tablet.swimlaneHeight,
      labelWidth: breakpoints.tablet.labelWidth,
      fontSize: breakpoints.tablet.fontSize,
      spacing: 7
    }
  } else {
    return {
      swimlaneHeight: breakpoints.desktop.swimlaneHeight,
      labelWidth: breakpoints.desktop.labelWidth,
      fontSize: breakpoints.desktop.fontSize,
      spacing: 8
    }
  }
}

/**
 * Generate loading animation styles
 */
export function generateLoadingStyles(): React.CSSProperties {
  const animations = RELEASE_TIMELINE_ANIMATIONS
  
  return {
    background: `linear-gradient(90deg, 
      transparent 0%, 
      rgba(255,255,255,0.4) 50%, 
      transparent 100%)`,
    backgroundSize: '200% 100%',
    animation: `shimmer ${animations.loading.duration} infinite`,
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '-200% 0' },
      '100%': { backgroundPosition: '200% 0' }
    }
  }
}

/**
 * Get category colors based on dark mode
 */
export function getCategoryColors(category: ReleaseCategory, isDarkMode: boolean = false) {
  const categoryConfig = RELEASE_TIMELINE_COLORS.categories[category]
  return isDarkMode ? categoryConfig.darkMode : categoryConfig
}

/**
 * Generate theme-aware styles
 */
export function generateThemeAwareStyles(
  theme: Theme,
  isDarkMode: boolean = false
): Record<string, React.CSSProperties> {
  const baseColors = isDarkMode
    ? {
        background: theme.palette.grey[900],
        surface: theme.palette.grey[800],
        border: theme.palette.grey[700],
        text: theme.palette.grey[100]
      }
    : {
        background: theme.palette.grey[50],
        surface: theme.palette.common.white,
        border: theme.palette.grey[300],
        text: theme.palette.grey[900]
      }

  return {
    container: {
      backgroundColor: baseColors.background,
      color: baseColors.text,
      border: `1px solid ${baseColors.border}`
    },
    surface: {
      backgroundColor: baseColors.surface,
      color: baseColors.text
    },
    border: {
      borderColor: baseColors.border
    }
  }
}
